import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Document, Page, pdfjs } from 'react-pdf';
import { useLanguage } from '../context/LanguageContext';
import { 
  FileText,
  Download,
  ExternalLink,
  BookOpen,
  ShoppingBag,
  Eye,
  X,
  ChevronLeft,
  ChevronRight,
  ZoomIn,
  ZoomOut,
  Loader2
} from 'lucide-react';

import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';

// Set up PDF.js worker
pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

const API_URL = process.env.REACT_APP_BACKEND_URL;
const LIFEWAVE_SHOP_URL = 'https://lifewave.com/ioanaalexandra/store/products';

const GuidesPage = () => {
  const [guides, setGuides] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPdf, setSelectedPdf] = useState(null);
  const [numPages, setNumPages] = useState(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [scale, setScale] = useState(1.0);
  const [pdfLoading, setPdfLoading] = useState(true);
  const [pdfError, setPdfError] = useState(null);
  const { language } = useLanguage();

  useEffect(() => {
    const fetchGuides = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/guides`);
        setGuides(response.data || []);
      } catch (error) {
        console.error('Error fetching guides:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchGuides();
  }, []);

  const onDocumentLoadSuccess = ({ numPages }) => {
    setNumPages(numPages);
    setPdfLoading(false);
    setPdfError(null);
  };

  const onDocumentLoadError = (error) => {
    console.error('PDF load error:', error);
    setPdfLoading(false);
    setPdfError(error);
  };

  const openPdfViewer = (guide) => {
    setSelectedPdf(guide);
    setPageNumber(1);
    setScale(1.0);
    setPdfLoading(true);
    setPdfError(null);
  };

  const closePdfViewer = () => {
    setSelectedPdf(null);
    setNumPages(null);
    setPageNumber(1);
  };

  const goToPrevPage = () => {
    setPageNumber(prev => Math.max(prev - 1, 1));
  };

  const goToNextPage = () => {
    setPageNumber(prev => Math.min(prev + 1, numPages || 1));
  };

  const zoomIn = () => {
    setScale(prev => Math.min(prev + 0.2, 2.5));
  };

  const zoomOut = () => {
    setScale(prev => Math.max(prev - 0.2, 0.5));
  };

  // Translations
  const translations = {
    en: {
      pageTitle: "Guides & Resources",
      subtitle: "Free guides to learn more about LifeWave technology",
      download: "Download",
      read: "Read Now",
      noGuides: "No guides available yet",
      shopTitle: "Ready to Get Started?",
      shopSubtitle: "Visit my official LifeWave store",
      shopButton: "Visit Store",
      page: "Page",
      of: "of",
      close: "Close",
      pdfError: "Unable to display this PDF in the browser. Please download it instead.",
      downloadInstead: "Download PDF"
    },
    ro: {
      pageTitle: "Ghiduri și Resurse",
      subtitle: "Ghiduri gratuite pentru a afla mai multe despre tehnologia LifeWave",
      download: "Descarcă",
      read: "Citește Acum",
      noGuides: "Nu există ghiduri disponibile încă",
      shopTitle: "Ești Gata să Începi?",
      shopSubtitle: "Vizitează magazinul meu oficial LifeWave",
      shopButton: "Vizitează Magazinul",
      page: "Pagina",
      of: "din",
      close: "Închide",
      pdfError: "Nu se poate afișa acest PDF în browser. Vă rugăm să îl descărcați.",
      downloadInstead: "Descarcă PDF"
    },
    de: {
      pageTitle: "Leitfäden & Ressourcen",
      subtitle: "Kostenlose Leitfäden, um mehr über LifeWave-Technologie zu erfahren",
      download: "Herunterladen",
      read: "Jetzt Lesen",
      noGuides: "Noch keine Leitfäden verfügbar",
      shopTitle: "Bereit Loszulegen?",
      shopSubtitle: "Besuchen Sie meinen offiziellen LifeWave-Shop",
      shopButton: "Shop Besuchen",
      page: "Seite",
      of: "von",
      close: "Schließen",
      pdfError: "Dieses PDF kann nicht im Browser angezeigt werden. Bitte laden Sie es herunter.",
      downloadInstead: "PDF Herunterladen"
    }
  };

  const t = translations[language] || translations['en'];

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-sky-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="guides-page">
      {/* PDF Viewer Modal */}
      {selectedPdf && (
        <div className="fixed inset-0 z-50 bg-black/90 flex flex-col">
          {/* Header */}
          <div className="bg-slate-900 px-4 py-3 flex items-center justify-between">
            <h3 className="text-white font-semibold truncate flex-1 mr-4">
              {selectedPdf.title}
            </h3>
            <div className="flex items-center gap-2">
              {/* Zoom controls */}
              <button
                onClick={zoomOut}
                className="p-2 text-white/70 hover:text-white hover:bg-white/10 rounded-lg transition-colors"
                title="Zoom out"
              >
                <ZoomOut className="w-5 h-5" />
              </button>
              <span className="text-white/70 text-sm min-w-[50px] text-center">
                {Math.round(scale * 100)}%
              </span>
              <button
                onClick={zoomIn}
                className="p-2 text-white/70 hover:text-white hover:bg-white/10 rounded-lg transition-colors"
                title="Zoom in"
              >
                <ZoomIn className="w-5 h-5" />
              </button>
              
              {/* Download button */}
              <a
                href={selectedPdf.file_url}
                target="_blank"
                rel="noopener noreferrer"
                className="p-2 text-white/70 hover:text-white hover:bg-white/10 rounded-lg transition-colors ml-2"
                title={t.download}
              >
                <Download className="w-5 h-5" />
              </a>
              
              {/* Close button */}
              <button
                onClick={closePdfViewer}
                className="p-2 text-white/70 hover:text-white hover:bg-white/10 rounded-lg transition-colors ml-2"
                title={t.close}
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* PDF Content */}
          <div className="flex-1 overflow-auto flex items-start justify-center p-4">
            {pdfLoading && !pdfError && (
              <div className="absolute inset-0 flex items-center justify-center">
                <Loader2 className="w-12 h-12 text-white animate-spin" />
              </div>
            )}
            {pdfError ? (
              <div className="flex flex-col items-center justify-center text-center p-8">
                <FileText className="w-16 h-16 text-white/50 mb-4" />
                <p className="text-white/80 mb-6 max-w-md">{t.pdfError}</p>
                <a
                  href={selectedPdf.file_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 bg-sky-500 hover:bg-sky-600 text-white px-6 py-3 rounded-xl font-medium transition-colors"
                >
                  <Download className="w-5 h-5" />
                  {t.downloadInstead}
                </a>
              </div>
            ) : (
              <Document
                file={selectedPdf.file_url}
                onLoadSuccess={onDocumentLoadSuccess}
                onLoadError={onDocumentLoadError}
                loading={null}
                className="max-w-full"
              >
                <Page 
                  pageNumber={pageNumber} 
                  scale={scale}
                  className="shadow-2xl"
                  loading={null}
                />
              </Document>
            )}
          </div>

          {/* Footer with pagination */}
          {numPages && (
            <div className="bg-slate-900 px-4 py-3 flex items-center justify-center gap-4">
              <button
                onClick={goToPrevPage}
                disabled={pageNumber <= 1}
                className="p-2 text-white/70 hover:text-white hover:bg-white/10 rounded-lg transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
              >
                <ChevronLeft className="w-6 h-6" />
              </button>
              <span className="text-white">
                {t.page} {pageNumber} {t.of} {numPages}
              </span>
              <button
                onClick={goToNextPage}
                disabled={pageNumber >= numPages}
                className="p-2 text-white/70 hover:text-white hover:bg-white/10 rounded-lg transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
              >
                <ChevronRight className="w-6 h-6" />
              </button>
            </div>
          )}
        </div>
      )}

      {/* Hero Section */}
      <section className="bg-gradient-to-br from-red-50 via-white to-orange-50 py-16">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <div className="inline-flex items-center gap-2 bg-red-100 text-red-700 px-4 py-2 rounded-full text-sm font-medium mb-4">
            <BookOpen className="w-4 h-4" />
            PDF
          </div>
          <h1 
            className="text-4xl sm:text-5xl font-bold mb-4"
            style={{
              background: 'linear-gradient(135deg, #dc2626 0%, #f97316 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
              fontFamily: "'Poppins', sans-serif"
            }}
          >
            {t.pageTitle}
          </h1>
          <p 
            className="text-lg text-slate-600 max-w-2xl mx-auto"
            style={{ fontFamily: "'Poppins', sans-serif" }}
          >
            {t.subtitle}
          </p>
        </div>
      </section>

      {/* Guides Grid */}
      <section className="py-16">
        <div className="max-w-6xl mx-auto px-4">
          {guides.length === 0 ? (
            <div className="text-center py-16">
              <FileText className="w-16 h-16 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-500 text-lg">{t.noGuides}</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {guides.map(guide => (
                <div 
                  key={guide.id}
                  className="bg-white rounded-2xl shadow-sm hover:shadow-lg transition-all duration-300 overflow-hidden group"
                >
                  {/* Cover Image or Icon */}
                  {guide.cover_image ? (
                    <div className="aspect-[4/3] overflow-hidden">
                      <img 
                        src={guide.cover_image} 
                        alt={guide.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      />
                    </div>
                  ) : (
                    <div className="aspect-[4/3] bg-gradient-to-br from-red-100 to-orange-100 flex items-center justify-center">
                      <FileText className="w-16 h-16 text-red-300" />
                    </div>
                  )}
                  
                  {/* Content */}
                  <div className="p-6">
                    <h2 
                      className="text-xl font-bold text-slate-900 mb-2"
                      style={{ fontFamily: "'Poppins', sans-serif" }}
                    >
                      {guide.title}
                    </h2>
                    
                    {guide.description && (
                      <p className="text-slate-600 text-sm mb-4 line-clamp-2">
                        {guide.description}
                      </p>
                    )}
                    
                    {/* Two buttons: Read & Download */}
                    <div className="flex gap-2">
                      <button
                        onClick={() => openPdfViewer(guide)}
                        className="flex-1 inline-flex items-center justify-center gap-2 bg-gradient-to-r from-sky-500 to-blue-600 hover:from-sky-600 hover:to-blue-700 text-white px-4 py-2.5 rounded-xl font-medium transition-all"
                      >
                        <Eye className="w-4 h-4" />
                        {t.read}
                      </button>
                      <a
                        href={guide.file_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center justify-center gap-2 bg-slate-100 hover:bg-slate-200 text-slate-700 px-4 py-2.5 rounded-xl font-medium transition-colors"
                        title={t.download}
                      >
                        <Download className="w-4 h-4" />
                      </a>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Shop CTA Banner */}
      <section className="py-16 bg-gradient-to-r from-sky-600 to-cyan-500">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <div className="inline-flex items-center gap-2 bg-white/20 text-white px-4 py-2 rounded-full text-sm font-medium mb-4 backdrop-blur-sm">
            <ShoppingBag className="w-4 h-4" />
            LifeWave
          </div>
          <h2 
            className="text-3xl font-bold text-white mb-3"
            style={{ fontFamily: "'Poppins', sans-serif" }}
          >
            {t.shopTitle}
          </h2>
          <p className="text-sky-100 mb-6">
            {t.shopSubtitle}
          </p>
          <button
            onClick={() => window.open(LIFEWAVE_SHOP_URL, '_blank')}
            className="inline-flex items-center gap-2 bg-white text-sky-600 hover:bg-sky-50 px-6 py-3 rounded-full font-bold transition-colors"
          >
            <ExternalLink className="w-5 h-5" />
            {t.shopButton}
          </button>
        </div>
      </section>
    </div>
  );
};

export default GuidesPage;
